import { useEffect, useMemo, useRef, useState } from 'react';
import './style.scss';
import 'leaflet.markercluster';
import { AttributionControl, FeatureGroup, MapContainer, TileLayer, ZoomControl, useMapEvents } from 'react-leaflet';
import { flushSync } from 'react-dom';
import { LatLng } from 'leaflet';
import useFetch from '../../../hooks/fetch.hook';
import { StopAreaApiModel } from '../../StopAreas/models/stop-area.api-model';
import type { IStopArea } from '../../../models/stop-area.model';
import { MicromobilityDataApiModel } from '../../../services/micromobility/models/micromobility-data.api-model';
import type { IMicromobilityData } from '../../../models/labs/micromobility/micromobility-data.model';
import OverlayBox from '../../Shared/OverlayBox';
import { TranslationService } from '../../../services/translation.service';
import MicromobilityFilters from '../../Shared/MicromobilityFilters';
import LocationAutocomplete, { type ILocationAutocompleteSelection } from '../../Shared/LocationAutocomplete';
import { LocationAutocompleteSelectionType } from '../../Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';
import { LocationType } from '../../../models/location-type.enum';
import ValidationInfo from '../../../validation/validation-info';
import type { IAutocomplete } from '../../Shared/AutocompleteBase';
import ClusteredMarkers from '../../Shared/Map/components/ClusteredMarkers';
import StopAreaClusterDivIcon from '../../StopAreas/StopAreaMap/markers/stop-area-cluster-div-icon';
import StationMarker from '../../Shared/Map/markers/StationMarker';
import { FormFactor } from '../../../models/labs/micromobility/form-factor.enum';
import DocklessVehicleClusterMarker from '../../Shared/Map/markers/dockless-vehicle.cluster-marker';
import DocklessVehicleClusterDivIcon from '../../Shared/Map/markers/dockless-vehicle-cluster.div-icon';
import CustomLocateControl from '../../Shared/Map/components/LocateControl';
import FilterControl from '../../Shared/Map/components/FilterControl';
import StopAreaClusterMarker from '../../StopAreas/StopAreaMap/cluster-markers/stop-area.cluster-marker';
import type { IGenericLocation } from '../../Shared/Map/markers/GenericLocationMarker';
import GenericLocationMarker from '../../Shared/Map/markers/GenericLocationMarker';
import { AnalyticsService } from '../../../services/analytics-service';
import { PropulsionType } from '../../../models/labs/micromobility/propulsion-type.enum';
import { IMicromobilityFilters } from '../../Shared/MicromobilityFilters/micromobility-filters.model';

// === CONSTANTS ===
const urlTemplateDefault = 'https://osm.vasttrafik.se/styles/osm_vt_basic/{z}/{x}/{y}.png';
const attribution = '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const attributionPrefix = '<a href="https://leafletjs.com" title="A JavaScript library for interactive maps">Leaflet</a>';
const initialZoomLevel = 12;
const zoomLevelThresholdForMicromobilitiy = 15;
const zoomLevelFocusOnSelectedLocation = 16;

function processReceivedStopAreaData(data: StopAreaApiModel[] | undefined, filtersSelection: IMicromobilityFilters): IStopArea[] {
  if (!filtersSelection.showStopAreas || !data) return [];
  return data.map(StopAreaApiModel.mapToBusinessModel) ?? [];
}

function processReceivedMicromobilityData(data: MicromobilityDataApiModel | undefined, filters: IMicromobilityFilters): IMicromobilityData {
  if (!data) return { stations: [], docklessVehicles: [] };
  const all = MicromobilityDataApiModel.mapToBusinessModel(data);
  return {
    stations: filters.showBikeStations ? all.stations : [],
    docklessVehicles: all.docklessVehicles.filter(
      (x) =>
        (x.formFactor === FormFactor.Bicycle && x.propulsionType === PropulsionType.ElectricAssist && filters.showElectricBikesVoi && x.operatorName === 'Voi') ||
        (x.formFactor === FormFactor.Scooter && ((filters.showScootersRyde && x.operatorName === 'Ryde') || (filters.showScootersVoi && x.operatorName === 'Voi')))
    ),
  };
}

function MicromobilitySearch() {
  // === STATE ===
  const [{ data: getStopAreasData }] = useFetch<StopAreaApiModel[]>('/api/stop-area-search/stopareas');
  const [{ data: getMicromobilityData }] = useFetch<MicromobilityDataApiModel>('/api/labs/micromobility');
  const [showFilters, setShowFilters] = useState(false);
  const [filtersSelection, setFiltersSelection] = useState<IMicromobilityFilters>({
    showStopAreas: true,
    showBikeStations: true,
    showElectricBikes: true,
    showElectricBikesVoi: true,
    showElectricBikesDott: true,
    showScooters: true,
    showScootersRyde: true,
    showScootersVoi: true,
    showScootersDott: true,
  });
  const [zoomLevelAboveMicromobilityThreshold, setZoomLevelAboveMicromobilityThreshold] = useState(initialZoomLevel >= zoomLevelThresholdForMicromobilitiy);
  const [selectedLocation, setSelectedLocation] = useState<ILocationAutocompleteSelection | undefined>(undefined);
  const [otherLocationMarker, setOtherLocationMarker] = useState<IGenericLocation | undefined>(undefined);
  const [updatingSelectedLocation, setUpdatingSelectedLocation] = useState(false);

  // === REFS ===
  const locationAutocompleteDesktopRef = useRef<IAutocomplete>(null!);
  const locationAutocompleteMobileRef = useRef<IAutocomplete>(null!);

  // === VARIABLES ===
  let map: L.Map | undefined;
  const stopAreas = processReceivedStopAreaData(getStopAreasData, filtersSelection);
  const stopAreaMarkers = useMemo(() => stopAreas.map((x) => new StopAreaClusterMarker(x)), [stopAreas]);
  const micromobilityData = processReceivedMicromobilityData(getMicromobilityData, filtersSelection);
  const stationMarkers = useMemo(() => micromobilityData.stations.map((x) => <StationMarker station={x} key={x.id} isHighlighted={false} />), [micromobilityData.stations]);
  const scooterMarkers = useMemo(
    () => micromobilityData.docklessVehicles.filter((x) => x.formFactor === FormFactor.Scooter).map((x) => new DocklessVehicleClusterMarker(x)),
    [micromobilityData.docklessVehicles]
  );
  const eletricBikeMarkers = useMemo(
    () => micromobilityData.docklessVehicles.filter((x) => x.formFactor === FormFactor.Bicycle && x.propulsionType === PropulsionType.ElectricAssist).map((x) => new DocklessVehicleClusterMarker(x)),
    [micromobilityData.docklessVehicles]
  );

  // === HELPERS ===
  function MapSetup() {
    map = useMapEvents({
      zoomend: () => {
        setZoomLevelAboveMicromobilityThreshold(map !== undefined && map.getZoom() >= zoomLevelThresholdForMicromobilitiy);
      },
    });
    map.getContainer().setAttribute('aria-label', TranslationService.get('common.map.map'));
    return null;
  }

  // === EFFECTS ===
  useEffect(() => {
    if (
      !selectedLocation ||
      selectedLocation.type !== LocationAutocompleteSelectionType.location ||
      !selectedLocation.location ||
      !selectedLocation.location.latitude ||
      !selectedLocation.location.longitude
    )
      return;

    const coordinates = new LatLng(selectedLocation.location.latitude, selectedLocation.location.longitude);
    if (updatingSelectedLocation) {
      map?.setView(coordinates, zoomLevelFocusOnSelectedLocation);
      setUpdatingSelectedLocation(false);
    }

    if (selectedLocation.location?.locationType === LocationType.stopArea) {
      const selectedStopAreaMarker = stopAreaMarkers.find((x) => x.id === selectedLocation?.location?.gid);
      if (!selectedStopAreaMarker) return;
      selectedStopAreaMarker.openPopup();
    }
  }, [map, selectedLocation, stopAreaMarkers, updatingSelectedLocation]);

  // === EVENT HANDLERS ===
  function handleOnOverlayBoxLoad() {
    // Copied from StopAreaMapHelper.moveBreadcrumbs
    const searchBox = document.getElementsByClassName('overlay-component')[0];
    const breadcrumbs = document.getElementById('micromobilitysearchpage-breadcrumbs');
    searchBox.insertBefore(breadcrumbs!, searchBox.firstChild);
    const firstChild = searchBox.firstChild as HTMLElement;
    firstChild.classList.remove('d-none');
  }

  function handleOnLocationSelected(item: ILocationAutocompleteSelection) {
    setUpdatingSelectedLocation(true);
    setSelectedLocation(item);
    if (item.location?.locationType === LocationType.metaStation || item.location?.locationType === LocationType.pointOfInterest || item.location?.locationType === LocationType.address) {
      setOtherLocationMarker({
        text: item.location.name ?? '',
        altText: `${TranslationService.get('common.map.selectedlocation')}: ${item.location.name ?? ''}`,
        latitude: item.location.latitude ?? 0,
        longitude: item.location.longitude ?? 0,
        autoOpenPopup: true,
      });
    } else {
      setOtherLocationMarker(undefined);
    }
    AnalyticsService.addEvent(AnalyticsService.LabsMicromobility, 'Plats vald', item.location?.locationType);
  }

  function handleOnFilterControlClick() {
    flushSync(() => {
      setShowFilters(!showFilters);
    });
    AnalyticsService.addEvent(AnalyticsService.LabsMicromobility, 'Öppna/stäng filter', showFilters ? 'Stäng' : 'Öppna');
    document.getElementById('mobile-filter-modal')?.focus();
  }

  function handleOnLocateControlClick() {
    AnalyticsService.addEvent(AnalyticsService.LabsMicromobility, 'Klick på min position');
  }

  return (
    <div
      className="micromobility-search position-relative d-md-flex flex-md-column flex-lg-row"
      role="search"
      aria-label={TranslationService.get('labs.micromobility.micromobilitysearchpage.searchlabel')}
    >
      <label id="location-autocomplete-mobile-label" htmlFor="location-autocomplete-mobile" className="visually-hidden">
        {TranslationService.get('labs.micromobility.micromobilitysearchpage.searchboxlabel')}
      </label>
      <LocationAutocomplete
        id="location-autocomplete-mobile"
        /* @ts-expect-error ts(2322) */
        ref={locationAutocompleteMobileRef}
        className="micromobility-search__location-autocomplete-mobile position-absolute shadow d-lg-none"
        locationTypes={[LocationType.stopArea, LocationType.metaStation, LocationType.address, LocationType.pointOfInterest]}
        placeholderText={TranslationService.get('labs.micromobility.micromobilitysearchpage.searchboxplaceholdertext')}
        listAriaLabelledBy="location-autocomplete-mobile-label"
        state={{ value: undefined, validationInfo: new ValidationInfo() }}
        onSelect={(item) => {
          locationAutocompleteMobileRef.current.clear();
          if (item) {
            handleOnLocationSelected(item);
          }
        }}
      />
      <OverlayBox
        headerText={TranslationService.get('labs.micromobility.micromobilitysearchpage.heading')}
        contentText={TranslationService.get('labs.micromobility.micromobilitysearchpage.textcontent')}
        contentTextCssClass=""
        absolutePositionedMd={false}
        className="d-none d-lg-block"
        onLoad={handleOnOverlayBoxLoad}
      >
        <label id="location-autocomplete-desktop-label" htmlFor="location-autocomplete-desktop" className="form-label">
          {TranslationService.get('labs.micromobility.micromobilitysearchpage.searchboxlabel')}
        </label>
        <LocationAutocomplete
          id="location-autocomplete-desktop"
          /* @ts-expect-error ts(2322) */
          ref={locationAutocompleteDesktopRef}
          className="mb-3"
          locationTypes={[LocationType.stopArea, LocationType.metaStation, LocationType.address, LocationType.pointOfInterest]}
          placeholderText={TranslationService.get('labs.micromobility.micromobilitysearchpage.searchboxplaceholdertext')}
          listAriaLabelledBy="location-autocomplete-desktop-label"
          state={{ value: undefined, validationInfo: new ValidationInfo() }}
          onSelect={(item) => {
            locationAutocompleteDesktopRef.current.clear();
            if (item) {
              handleOnLocationSelected(item);
            }
          }}
        />
        <fieldset>
          <legend className="mb-2 desktop-legend">{TranslationService.get('labs.micromobility.micromobilitysearchpage.filtersubheading')}</legend>
          <MicromobilityFilters id="desktop-filters" selection={filtersSelection} showSelectionStopAreas={true} onChange={setFiltersSelection} />
        </fieldset>
      </OverlayBox>
      <MapContainer
        center={[57.69676, 11.99392]}
        zoom={initialZoomLevel}
        scrollWheelZoom={true}
        className={`vt-map text-center p-3 p-md-5 micromobility-search__map flex-grow-1 micromobility-map`}
        zoomControl={false}
        attributionControl={false}
        preferCanvas={false}
      >
        <MapSetup />
        <TileLayer attribution={attribution} url={urlTemplateDefault} maxNativeZoom={18} maxZoom={20} />
        <AttributionControl position="bottomright" prefix={attributionPrefix} />
        {zoomLevelAboveMicromobilityThreshold && <FeatureGroup>{stationMarkers}</FeatureGroup>}
        {zoomLevelAboveMicromobilityThreshold && (
          <ClusteredMarkers markers={scooterMarkers} disableClusteringAtZoom={17} iconCreateFunction={(c) => new DocklessVehicleClusterDivIcon(c, FormFactor.Scooter, PropulsionType.Electric)} />
        )}
        {zoomLevelAboveMicromobilityThreshold && (
          <ClusteredMarkers
            markers={eletricBikeMarkers}
            disableClusteringAtZoom={17}
            iconCreateFunction={(c) => new DocklessVehicleClusterDivIcon(c, FormFactor.Bicycle, PropulsionType.ElectricAssist)}
          />
        )}
        {otherLocationMarker && <GenericLocationMarker marker={otherLocationMarker} />}
        <ClusteredMarkers markers={stopAreaMarkers} disableClusteringAtZoom={15} iconCreateFunction={(c) => new StopAreaClusterDivIcon(c)} />
        <CustomLocateControl position="bottomright" onClick={handleOnLocateControlClick} />
        <ZoomControl position="bottomright" zoomInTitle={TranslationService.get('common.map.zoomin')} zoomOutTitle={TranslationService.get('common.map.zoomout')} />
        <FilterControl onClick={handleOnFilterControlClick} className="d-lg-none" />
      </MapContainer>
      {showFilters && (
        <div id="mobile-filter-modal" className="micromobility-search__filter-modal position-absolute bg-white rounded shadow p-3 d-lg-none" tabIndex={-1}>
          <button
            type="button"
            className="btn-close micromobility-search__close-filter-modal-button position-absolute px-2 py-1"
            aria-label={TranslationService.get('common.close')}
            onClick={() => {
              setShowFilters(false);
              AnalyticsService.addEvent(AnalyticsService.LabsMicromobility, 'Öppna/stäng filter', 'Stäng');
            }}
          ></button>
          <fieldset>
            <legend>{TranslationService.get('labs.micromobility.micromobilitysearchpage.filterheading')}</legend>
            <div className="mb-2">{TranslationService.get('labs.micromobility.micromobilitysearchpage.filtersubheading')}</div>
            <MicromobilityFilters id="mobile-filters" showSelectionStopAreas={true} selection={filtersSelection} onChange={setFiltersSelection} />
          </fieldset>
        </div>
      )}
    </div>
  );
}

export default MicromobilitySearch;
